import { DensityUnits } from "@/libs/units/density";
import { LiquidFlowRateUnits } from "@/libs/units/flowRate";
import { GenericUnits } from "@/libs/units/generic";
import { LengthUnits, LargeLengthUnits } from "@/libs/units/length";
import { MassUnits } from "@/libs/units/mass";
import { PercentageUnits } from "@/libs/units/percentage";
import { PowerUnits } from "@/libs/units/power";
import { PressureUnits } from "@/libs/units/pressure";
import { TemperatureUnits } from "@/libs/units/temperature";
import { EquipmentCalculatorSectionKey } from "../../calculationTypes";
import type { ConverterInputProps } from "@/components/types";
import type { Section, SectionDeps } from "../types";
import { ScenarioDetailSectionKey, ScenarioDetailSectionTitle } from "../types";

export const buildPumpSideCalculatorSection = ({
  scenarioCalculations,
  wellProperties,
  differentialPressureTargetUnit,
  getHydraulicPowerUnit,
  getPressureUnit
}: SectionDeps): Section => ({
  id: EquipmentCalculatorSectionKey.PUMP,
  hash: ScenarioDetailSectionKey.PUMP,
  label: ScenarioDetailSectionTitle.PUMP,
  routeName: "SizingParameters",
  aggregateDisplays: [
    {
      label: "Target Liquid Rate",
      base: LiquidFlowRateUnits.BarrelsPerDay,
      model: LiquidFlowRateUnits.options(),
      mode: "convert",
      value: scenarioCalculations.desiredLiquidRateSTDB.value
    },
    {
      label: "Discharge Fluid Density",
      base: DensityUnits.PoundsPerCubicFoot,
      model: DensityUnits.options([
        DensityUnits.API,
        DensityUnits.SpecificGravity
      ]),
      mode: "convert",
      value: scenarioCalculations.dischargeFluidDensityLBMFT3.value
    },
    {
      label: "Required TDH",
      value:
        scenarioCalculations.pumpCalculations.aggregate
          .requiredTotalDynamicHeadFt.value,
      base: LengthUnits.Feet,
      model: LargeLengthUnits.options(),
      mode: "convert"
    },
    {
      label: "Produced TDH",
      value:
        scenarioCalculations.pumpCalculations.aggregate
          .producedTotalDynamicHeadFt.value,
      base: LengthUnits.Feet,
      model: LargeLengthUnits.options(),
      mode: "convert"
    },
    {
      label: "Gas Head Rerate",
      value:
        scenarioCalculations.pumpCalculations.aggregate.gasHeadRerateRatio
          .value,
      base: PercentageUnits.Ratio,
      target: PercentageUnits.Percentage,
      model: PercentageUnits.options(),
      mode: "read"
    },
    {
      label: "Casing Flow Regime",
      value: scenarioCalculations.casingFlowRegime.value,
      base: GenericUnits.Generic,
      model: [GenericUnits.Generic],
      mode: "read"
    },
    {
      label: "Tubing Flow Regime",
      value:
        scenarioCalculations.fluidPropertiesInsideTubingContext.value
          ?.fluidFrictionProperties.flowRegime,
      base: GenericUnits.Generic,
      model: [GenericUnits.Generic],
      mode: "read"
    },
    {
      label: "Tubing Friction Loss",
      value: scenarioCalculations.tubingFrictionLossPSIA.value,
      base: PressureUnits.PoundsPerSquareInchAbsolute,
      model: PressureUnits.options(),
      mode: "convert"
    },
    {
      label: "Casing Friction Loss",
      value: scenarioCalculations.casingFrictionLossPSIA.value,
      base: PressureUnits.PoundsPerSquareInchAbsolute,
      model: PressureUnits.options(),
      mode: "convert",
      decimalPlaces: 4
    },
    {
      label: "Tubing Internal Diameter @ Pump Discharge",
      value: scenarioCalculations.tubingInternalDiameterAtPumpDischargeIn.value,
      base: LengthUnits.Inches,
      model: LengthUnits.options(),
      mode: "convert"
    },
    {
      label: "Required Discharge Pressure",
      value: scenarioCalculations.isScenarioEspShore.value
        ? scenarioCalculations.pumpCalculations.aggregate
            .requiredDischargePressurePSIA.value
        : wellProperties.value?.hps_required_discharge_pressure_psia,
      base: PressureUnits.PoundsPerSquareInchAbsolute,
      model: PressureUnits.options(),
      mode: "convert"
    },
    {
      label: "Produced Discharge Pressure",
      value:
        scenarioCalculations.pumpCalculations.aggregate
          .producedDischargePressurePSIA.value,
      base: PressureUnits.PoundsPerSquareInchAbsolute,
      model: PressureUnits.options(),
      mode: "convert"
    },
    ...(!scenarioCalculations.isScenarioEspShore.value
      ? ([
          {
            label: "Differential Pressure",
            value:
              scenarioCalculations.pumpCalculations.aggregate
                .differentialPressurePSIA.value,
            base: PressureUnits.PoundsPerSquareInchAbsolute,
            target: differentialPressureTargetUnit.value,
            model: PressureUnits.optionsForDifferential(),
            mode: "convert"
          }
        ] as ConverterInputProps[])
      : []),
    {
      label: "Discharge Temperature",
      value:
        scenarioCalculations.pumpCalculations.aggregate.dischargeTemperatureF
          .value,
      base: TemperatureUnits.Fahrenheit,
      model: TemperatureUnits.options(),
      mode: "convert"
    },
    {
      label: scenarioCalculations.isScenarioEspShore.value
        ? "Thrust Load"
        : "Thrust Load at Duty Point",
      base: MassUnits.Pounds,
      model: MassUnits.options(),
      value:
        scenarioCalculations.pumpCalculations.aggregate.thrustLoadPounds.value,
      mode: "read"
    },
    {
      label: scenarioCalculations.isScenarioEspShore.value
        ? "Shut-in Thrust Load"
        : "Max Thrust Load",
      value:
        scenarioCalculations.pumpCalculations.aggregate.shutInThrustLoadPounds
          .value,
      base: MassUnits.Pounds,
      model: MassUnits.options(),
      mode: "convert"
    },
    ...(() => {
      if (scenarioCalculations.isScenarioEspShore.value) {
        return [
          {
            label: "Shut-In Pressure @ Wellhead",
            value:
              scenarioCalculations.pumpCalculations.aggregate
                .wellheadShutInBurstPressurePSIA.value,
            base: PressureUnits.PoundsPerSquareInchAbsolute,
            model: PressureUnits.options(),
            mode: "convert"
          },
          {
            label: "Shut-In Pressure @ Discharge",
            value:
              scenarioCalculations.pumpCalculations.aggregate
                .dischargeShutInBurstPressurePSIA.value,
            base: PressureUnits.PoundsPerSquareInchAbsolute,
            model: PressureUnits.options(),
            mode: "convert"
          }
        ];
      }

      return [
        {
          label: "Shut-In Pressure Rise",
          value:
            scenarioCalculations.pumpCalculations.aggregate
              .shutInPressureRisePSIA.value,
          base: PressureUnits.PoundsPerSquareInchAbsolute,
          model: PressureUnits.options(),
          mode: "convert"
        },
        {
          label: "Shut-In Pressure @ Discharge",
          value:
            scenarioCalculations.pumpCalculations.aggregate
              .dischargeShutInBurstPressurePSIA.value,
          base: PressureUnits.PoundsPerSquareInchAbsolute,
          model: PressureUnits.options(),
          mode: "convert"
        }
      ];
    })(),
    ...(scenarioCalculations.isScenarioEspShore.value
      ? ([
          {
            label: "Shut-in Burst Percentage (STD)",
            value:
              scenarioCalculations.pumpCalculations.aggregate
                .stdShutInBurstPercentage.value,
            target: PercentageUnits.Percentage,
            base: PercentageUnits.Ratio,
            model: PercentageUnits.options(),
            mode: "read"
          },
          {
            label: "Shut-in Burst Percentage (HS)",
            value:
              scenarioCalculations.pumpCalculations.aggregate
                .hsShutInBurstPercentage.value,
            target: PercentageUnits.Percentage,
            base: PercentageUnits.Ratio,
            model: PercentageUnits.options(),
            mode: "read"
          },
          {
            label: "Operating Burst Percentage (STD)",
            value:
              scenarioCalculations.pumpCalculations.aggregate
                .stdOperatingBurstPercentage.value,
            target: PercentageUnits.Percentage,
            base: PercentageUnits.Ratio,
            model: PercentageUnits.options(),
            mode: "read"
          },
          {
            label: "Operating Burst Percentage (HS)",
            value:
              scenarioCalculations.pumpCalculations.aggregate
                .hsOperatingBurstPercentage.value,
            target: PercentageUnits.Percentage,
            base: PercentageUnits.Ratio,
            model: PercentageUnits.options(),
            mode: "read"
          }
        ] as ConverterInputProps[])
      : []),
    {
      label: "Pump Efficiency",
      value:
        scenarioCalculations.pumpCalculations.aggregate.pumpEfficiencyRatio
          .value,
      target: PercentageUnits.Percentage,
      base: PercentageUnits.Ratio,
      model: PercentageUnits.options(),
      mode: "read"
    },
    ...(() => {
      const pumps = scenarioCalculations.descendingOrderPumps.value;
      return pumps.map(pump => {
        const pumpCalc =
          scenarioCalculations.pumpCalculations.individual.value[pump.id];
        const rorBep = pumpCalc?.rorBep;
        const intakeFlowBBLD =
          pumpCalc?.intakeStageCalculations?.intakeFluidPropertiesContext
            .flowRatesInSitu?.fluidFlowRateBBLD;
        return {
          label: `Percent of BEP (${pump.r_pump?.series_model ?? "Pump"})`,
          value:
            rorBep !== undefined &&
            rorBep > 0 &&
            intakeFlowBBLD !== undefined &&
            intakeFlowBBLD > 0
              ? intakeFlowBBLD / rorBep
              : undefined,
          base: PercentageUnits.Ratio,
          target: PercentageUnits.Percentage,
          model: PercentageUnits.options(),
          mode: "read"
        };
      });
    })(),
    ...(() => {
      if (scenarioCalculations.isScenarioEspShore.value) {
        return [];
      }

      const aggregate = scenarioCalculations.pumpCalculations.aggregate;

      return [
        {
          label: "Max HP",
          value: aggregate.totalMaxBrakeHP.value,
          target: getHydraulicPowerUnit().value,
          base: PowerUnits.HorsePower,
          model: PowerUnits.options(),
          mode: "convert"
        },
        {
          label: "Max Pressure @ Discharge",
          value: aggregate.totalMaxDischargePressurePSIA.value,
          target: getPressureUnit("Max Housing Pressure.convert.PSIA").value,
          base: PressureUnits.PoundsPerSquareInchAbsolute,
          model: PressureUnits.options(),
          mode: "convert"
        }
      ];
    })(),
    {
      label: "Axial Bolt Load @ Discharge",
      value:
        scenarioCalculations.pumpCalculations.aggregate.axialBoltLoadRatio
          .value,
      target: PercentageUnits.Percentage,
      base: PercentageUnits.Ratio,
      model: PercentageUnits.options(),
      mode: "read"
    },
    {
      label: "Axial Shut-In Bolt Load @ Discharge",
      value:
        scenarioCalculations.pumpCalculations.aggregate.axialBoltLoadShutInRatio
          .value,
      target: PercentageUnits.Percentage,
      base: PercentageUnits.Ratio,
      model: PercentageUnits.options(),
      mode: "read"
    }
  ] as ConverterInputProps[]
});
